// load-save-view.ts — the in-game Load / Save game DOM overlay (HTML/DOM UI shell plan,
// Phase 5). Renders the canvas kidfLoadGame (1038) / kidfSaveGame (1039) forms — the SAME
// slot-list ShellForm (NO text entry; the save NAME is the level title). Load is reached
// from the pause-menu LOAD GAME and the LOSE-summary LOAD SAVED; Save from the pause-menu
// SAVE GAME. Both funnel through env.pickLoadGameStream / env.saveGame, which branch to this
// overlay when env.domLoadGame / env.domSaveGame is set.
//
// DISPLAY-ONLY: this view PICKS a slot and reports it via onPick(slot) (onCancel for BACK /
// Cancel / backdrop / Esc — the FORM default kidcCancel). The HOST opens the load stream /
// writes the save (loadsave.ts + gamesession.ts) exactly as the canvas DoModal does — this
// view constructs no stream and touches no save store.
//
// forms.ini.pp kidfLoadGame 1038 / kidfSaveGame 1039 (FORM 0 0 160 160, FORMBACKCOLOR center):
//   LIST=kidcGameList (3 19 153 115) kifntShadow                       ← the slot list
//   LABEL=0           (38 6 84 0) "LOAD SAVED GAME"/"SAVE GAME" kifntTitle center
//   BUTTON=kidcOk     (31 140 40 12) "LOAD"/"Save"
//   BUTTON=kidcCancel (86 140 40 12) "BACK"/"Cancel"

import type { ButtonArt } from '../title-art.ts';
import {
  COL_BG,
  COL_FG,
  el,
  formScale,
  at,
  formTitle,
  makeStageButton,
  buttonWidthU,
  stageListBox,
  stageListItem,
  type StageButtonSize,
} from '../chrome.ts';
import { saveSlotRows } from '../saves-io.ts';
import type { SaveRow } from '../menu-controller.ts';

// forms.ini.pp kidfLoadGame 1038 / kidfSaveGame 1039 (FORM 0 0 160 160), in design units.
const FORM = { w: 160, h: 160 };
const LIST = { x: 3, y: 19, w: 153, h: 115 };
const BTN_Y = 140;
const BTN_H = 12;

export interface LoadSaveViewDeps {
  /** the overlay container (created by the host; this view owns its contents). */
  root: HTMLElement;
  /** 'load' (LOAD SAVED GAME / LOAD / BACK) or 'save' (SAVE GAME / Save / Cancel). */
  mode: 'load' | 'save';
  /** occupied saves (slot, name, whenMs) — the makeMenuSaves(store).list(); empties are
   *  filled in by saveSlotRows. */
  rows: SaveRow[];
  /** the initially-selected slot (saves-io defaultSaveSlot — most-recent for load, the
   *  next slot for save). */
  defaultSlot: number;
  /** the raw 9-slice button pieces — faithful chrome; absent ⇒ a CSS-button fallback. */
  buttonArt?: ButtonArt;
  /** the shell title-screen backdrop (titlescreenbkgd) as a CSS url — the canvas LoadGameForm
   *  is a ShellForm, so it COVERS the game with the menu backdrop (not a floating panel).
   *  Absent ⇒ the solid COL_BG dark cover (the canvas kiclrMenuBack fallback). */
  titleBg?: string;
  /** play a GUI sfx (ksfxGui*) on a button press, wired to the host SoundMgr. */
  playSfx?: (sfx: number) => void;
  /** OK (LOAD / Save) with the selected slot → the host opens the stream / writes the save. */
  onPick: (slot: number) => void;
  /** BACK / Cancel / backdrop / Esc (the FORM default kidcCancel) → the host resumes. */
  onCancel: () => void;
}

export class LoadSaveView {
  private readonly d: LoadSaveViewDeps;
  private readonly btn: (label: string, on: () => void, size: StageButtonSize) => HTMLButtonElement;
  /** the selected slot (the list highlight + what OK reports). */
  private sel: number;
  /** guards against a double-result (a click + an Esc landing together). */
  private resolved = false;

  constructor(d: LoadSaveViewDeps) {
    this.d = d;
    this.sel = d.defaultSlot;
    const mk = makeStageButton(d);
    this.btn = (label, on, size) => mk(label, on, size);
    const s = d.root.style;
    // a fixed, full-window MODAL overlay at the in-game modal z-layer (20). pointer-events:all
    // so clicks reach us (and the canvas gets none); a tap outside the form = Cancel.
    s.position = 'fixed';
    s.inset = '0';
    s.zIndex = '20';
    s.color = COL_FG;
    s.userSelect = 'none';
    s.pointerEvents = 'all';
    s.overflow = 'hidden';
    // the canvas LoadGameForm is a ShellForm → it COVERS the screen with the menu backdrop
    // (the title-pattern), hiding the game; the form content sits ON it (no solid panel).
    s.backgroundColor = COL_BG; // the kiclrMenuBack base / no-art fallback
    if (d.titleBg !== undefined) {
      s.backgroundImage = `url(${d.titleBg})`;
      s.backgroundSize = 'cover';
      s.backgroundPosition = 'center';
    }
    d.root.addEventListener('pointerdown', this.onBackdrop);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('keydown', this.onKeyDown);
    this.render();
  }

  private readonly onResize = (): void => {
    this.render();
  };

  // a tap NOT on the form → Cancel (the FORM default kidcCancel).
  private readonly onBackdrop = (e: PointerEvent): void => {
    const stage = this.d.root.querySelector('[data-loadsave-stage]');
    if (stage !== null && e.target instanceof Node && stage.contains(e.target)) return;
    this.resolve(null);
  };

  // Esc → Cancel (kidcCancel — the FORM default).
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.resolve(null);
    }
  };

  destroy(): void {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('keydown', this.onKeyDown);
    this.d.root.removeEventListener('pointerdown', this.onBackdrop);
  }

  /** report the choice ONCE (the host unmounts + routes to the continuation). */
  private resolve(slot: number | null): void {
    if (this.resolved) return;
    this.resolved = true;
    if (slot === null) this.d.onCancel();
    else this.d.onPick(slot);
  }

  render(): void {
    const s = formScale();
    const stageW = FORM.w * s,
      stageH = FORM.h * s;
    // the 160×160 form content, centred on the window like the canvas ShellForm (and the
    // shell LOAD SAVED GAME screen — they share saveSlotRows + the chrome list/title
    // primitives, so they render identically). TRANSPARENT: the title/buttons sit on the
    // menu backdrop (root), only the LIST is boxed — exactly the canvas look (NOT a solid panel).
    const stage = el('div', {
      position: 'absolute',
      width: `${stageW}px`,
      height: `${stageH}px`,
      boxSizing: 'border-box',
      left: `${Math.round((window.innerWidth - stageW) / 2)}px`,
      top: `${Math.round((window.innerHeight - stageH) / 2)}px`,
    });
    stage.dataset.loadsaveStage = '1';

    // LABEL (38,6,84) center kifntTitle — full-width centred (the canvas centres a too-wide
    // title on its anchor; full-width keeps it on-form, matching the shell load screen).
    const title = this.d.mode === 'load' ? 'LOAD SAVED GAME' : 'SAVE GAME';
    stage.append(at(formTitle(title, s), s, 0, 6, FORM.w));

    // the slot LIST (3,19,153,115) — occupied "HH:MM name", empties "-- Empty --"; click to
    // select, double-click to activate (load/overwrite the slot).
    const list = stageListBox(s);
    list.dataset.loadsaveList = '1';
    for (const r of saveSlotRows(this.d.rows)) {
      list.append(
        stageListItem(r.text, r.slot === this.sel, s, {
          onSelect: () => {
            this.sel = r.slot;
            this.render();
          },
          onActivate: () => this.resolve(r.slot),
          playSfx: this.d.playSfx,
        }),
      );
    }
    stage.append(at(list, s, LIST.x, LIST.y, LIST.w, LIST.h));

    // kidcOk (LOAD/Save) + kidcCancel (BACK/Cancel) — each SIZED to its label via the shared
    // buttonWidthU then CENTRED as a pair on the 160-form (the fixed 40u boxes cramped the wide
    // WI face). LOAD is always enabled like the canvas (an empty slot is a no-op handled by
    // the host's re-show + message-box loop).
    const okLabel = this.d.mode === 'load' ? 'LOAD' : 'Save';
    const cancelLabel = this.d.mode === 'load' ? 'BACK' : 'Cancel';
    const wOk = buttonWidthU(okLabel, BTN_H, s);
    const wCancel = buttonWidthU(cancelLabel, BTN_H, s);
    const gapU = 5;
    let bx = Math.max(0, (FORM.w - (wOk + wCancel + gapU)) / 2);
    const ok = this.btn(okLabel, () => this.resolve(this.sel), { w: wOk * s, h: BTN_H * s });
    ok.dataset.loadsaveOk = '1';
    stage.append(at(ok, s, bx, BTN_Y));
    bx += wOk + gapU;
    const cancel = this.btn(cancelLabel, () => this.resolve(null), {
      w: wCancel * s,
      h: BTN_H * s,
    });
    cancel.dataset.loadsaveCancel = '1';
    stage.append(at(cancel, s, bx, BTN_Y));

    this.d.root.replaceChildren(stage);
  }
}
